import { onBeforeUnmount, onMounted, ref } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { anyAppModalOpen } from './useModalRegistry';

/*
 * Trigger logic for the forum offer popup. Mounted once, in AppLayout.
 *
 * The popup is the whole offer -- there is no landing page for any campaign --
 * so there are two ways in:
 *   A. FORCED. The campaign's entry route (/bhw, /promo/{slug}) redirected the
 *      visitor here and named the campaign in `forcePromo`. They clicked the
 *      forum link on purpose, so the popup opens promptly and skips the
 *      politeness gates below.
 *   B. ATTRIBUTED. Anyone carrying a campaign's utm_source, anywhere else on the
 *      site, long after the fact. Polite, capped, and easy to say no to.
 *
 * (B) is deliberately client-side. The homepage and the other public pages can
 * be served as a Cloudflare edge-cache HIT, so server-rendered HTML must never
 * carry per-visitor state -- that is the July 2026 /fr PII incident. The edge
 * worker already bypasses the cache for any utm_* param, so the first tagged
 * hit reaches Laravel and gets the attribution cookie set before this runs.
 *
 * MULTI-CAMPAIGN. Every campaign is a row in the server's registry, and this
 * file resolves the visitor to EXACTLY ONE of them before anything else happens.
 * The single most important compatibility rule is that 'bhw' is a permanent
 * member of that registry: `_jsms_attr.first` is written once and never rewritten
 * (CaptureTrafficSource), so hundreds of existing visitors carry
 * utm_source=bhw for a year and must keep being recognised.
 *
 * PROP COMPATIBILITY. The server dual-emits the old single-campaign props
 * (`bhw`, `bhwForcePopup`) alongside the new ones (`promos`, `forcePromo`).
 * This file reads the new pair when it is there and falls back to the old pair
 * when it is not, so a browser holding a cached bundle and a server that has not
 * shipped the new props yet both keep working -- in either direction.
 */

// Politeness state, not fraud control -- this audience will clear it, and that
// is fine: "at most one EUR 3.00 per account per campaign" is a database unique
// index. localStorage rather than a cookie: no request-header bloat, no
// interaction with cookie encryption, and no effect on the edge cache key.
//
// One bucket per campaign now. The pre-multi-campaign bucket is migrated on read
// and still written for BHW (see writeState): a visitor who already said no must
// not be re-pitched just because the bundle they load changed.
const LEGACY_STORAGE_KEY = 'jsms_bhw_popup_v1';
const STORAGE_PREFIX = 'jsms_promo_popup_v1:';
// Not localStorage: "we already pitched a promo on this visit" is a property of
// the visit, and it must expire when the tab closes rather than compete with the
// per-campaign frequency cap.
const SESSION_STORAGE_KEY = 'jsms_promo_popup_v1:session';

const MAX_SHOWINGS = 2;
const MIN_GAP_MS = 86_400_000;   // never twice within 24 h

// A forced visitor is mid-intent and staring at a homepage that says nothing
// about the offer, so the wait is only long enough for the page to settle;
// everywhere else it waits long enough not to interrupt.
const DELAY_FORCED_MS = 1200;
const DELAY_ELSEWHERE_MS = 6000;

// Forced showings, and only forced ones, retry the late checks. The landing page
// used to carry the offer in its own body, so a popup swallowed by the consent
// banner cost nothing; now the popup IS the offer, and the two things most likely
// to swallow it are exactly what this audience does -- a first-ever visit (so
// CookieScript's banner is up) and opening a forum link into a background tab.
// Bounded, because a visitor who leaves the tab parked for a minute has moved on.
const FORCED_RETRY_MS = 1000;
const FORCED_RETRY_LIMIT = 60;

/*
 * An allowlist, not a denylist. A page added next year gets no popup until
 * someone deliberately adds it here, which is what keeps the offer off every
 * checkout and payment surface -- AddFunds, CreditCardPayment, CryptoPayment,
 * PaymentSuccess, PaymentFailed, Order, Request, Hire/*, Auth/*, Webhooks*,
 * SupportPanel/*, Admin/*, Marketing/* -- without anyone having to remember
 * that this file exists. It also bounds the forced path: 'FigmaWelcome' is what
 * the entry routes redirect to, localized or not.
 *
 * ONE list for every campaign, deliberately. A campaign may narrow where it is
 * pitched (that is a server-side decision about its own copy), but nothing
 * per-campaign may widen this: it is the payment-surface guarantee, and a
 * per-campaign allowlist is a per-campaign chance to get it wrong.
 */
const ALLOWED = new Set([
    'FigmaWelcome',
    'Hdiw',
    'Faq',
    'Support',
    'Blog/Index',
    'Blog/Show',
    'RealNumbers',
    'MyAccount',
    'MyOrders',
    'Referrals',
]);

// Claim states where the offer is spent or dead and re-pitching it is a lie.
const TERMINAL_STATUSES = new Set(['credited', 'revoked', 'expired']);

const ATTRIBUTION_COOKIE = '_jsms_attr';

// BHW's copy lives in its own lang group and stays byte-identical; every other
// campaign renders the generic `promo` group with {forum} interpolated.
const LEGACY_SLUG = 'bhw';

/*
 * What BHW looked like before there was a registry.
 *
 * Used for exactly one case: a GUEST, whose props deliberately carry no per-user
 * promo state at all. Without this, a server that emits the registry only for
 * logged-in users would stop pitching the offer to the entire signed-out half of
 * the BHW audience -- silently, since "no registry" is indistinguishable from
 * "promo off". Guests still confirm the kill switch live in promoIsLive(), so
 * this can only ever schedule a popup, never show one for a dead promo.
 *
 * Deliberately NOT applied to logged-in visitors: for them an absent registry
 * IS the kill switch, exactly as `! page.props.bhw` was before.
 */
const LEGACY_GUEST_CAMPAIGN = Object.freeze({
    slug: LEGACY_SLUG,
    name: 'BlackHatWorld',
    amountEur: '3.00',
    threadUrl: null,
    entryUrl: '/bhw',
    utmSource: LEGACY_SLUG,
    slaHours: 24,
    minForumAccountAgeMonths: 3,
    status: null,
    code: null,
});

// Memoised for the lifetime of the tab: one status check per visitor, not one per
// navigation. Staleness is bounded by how long the tab stays open, which is the
// point -- the thing we are avoiding is a week-old answer baked into cached HTML.
let promoStatusPromise = null;
let legacyStatusPromise = null;

/**
 * The campaign the popup is currently about.
 *
 * Module state so BhwOfferModal can read it without AppLayout having to pass it
 * down; AppLayout may also bind `campaign` from the return value below, and the
 * modal prefers the prop when it gets one. Written only from browser-only code
 * paths (onMounted and its timers), so SSR never touches it.
 */
export const activePromo = ref(null);

/** Which lang group renders this campaign's copy. BHW's is frozen and separate. */
export function promoMessageGroup(campaign) {
    return campaign?.slug === LEGACY_SLUG ? 'bhw' : 'promo';
}

function readCookieAttribution() {
    try {
        const raw = document.cookie.split('; ').find((c) => c.startsWith(`${ATTRIBUTION_COOKIE}=`));
        if (! raw) {
            return null;
        }

        return JSON.parse(decodeURIComponent(raw.slice(ATTRIBUTION_COOKIE.length + 1)));
    } catch {
        return null;
    }
}

/**
 * The utm_source values this visitor could be attributed to, strongest first.
 *
 * Live click beats last-touch beats first-touch. `_jsms_attr` is written by
 * CaptureTrafficSource for a year and is in `encryptCookies(except: [...])`, so
 * JS can read it; its shape is `{first: {...}, last: {..., at}}`. Reading it here
 * means the popup survives internal navigation and session expiry with zero
 * server code and zero per-visitor state in cacheable HTML.
 */
function attributionSources() {
    if (typeof window === 'undefined') {
        return [];
    }

    const sources = [];
    const live = new URLSearchParams(window.location.search).get('utm_source');
    if (live) {
        sources.push(live);
    }

    const cookie = readCookieAttribution();
    if (cookie?.last?.utm_source) {
        sources.push(cookie.last.utm_source);
    }
    if (cookie?.first?.utm_source) {
        sources.push(cookie.first.utm_source);
    }

    return sources;
}

/**
 * Resolve a visitor to at most ONE of `campaigns` by attribution.
 *
 * `campaigns` is any iterable of campaign-shaped objects ({slug, utmSource}); the
 * caller decides which campaigns are eligible, this only decides which of them
 * the visitor belongs to. Exported because the My Account card has the same
 * question to answer and must answer it the same way.
 */
export function attributedPromoSlug(campaigns) {
    const list = Array.isArray(campaigns) ? campaigns : Object.values(campaigns ?? {});
    if (! list.length) {
        return null;
    }

    // utm_source is its own column server-side and need not equal the slug, so
    // both spellings resolve.
    const bySource = new Map();
    list.forEach((campaign) => {
        if (! campaign?.slug) {
            return;
        }
        bySource.set(campaign.slug, campaign.slug);
        if (campaign.utmSource) {
            bySource.set(campaign.utmSource, campaign.slug);
        }
    });

    for (const source of attributionSources()) {
        const slug = bySource.get(source);
        if (slug) {
            return slug;
        }
    }

    return null;
}

/** Fill in the fields a campaign row may legitimately omit. */
function normalizeCampaign(slug, raw) {
    return {
        ...raw,
        slug,
        name: raw?.name ?? slug,
        amountEur: raw?.amountEur ?? '3.00',
        threadUrl: raw?.threadUrl ?? null,
        entryUrl: raw?.entryUrl ?? `/promo/${slug}`,
        utmSource: raw?.utmSource ?? slug,
        slaHours: raw?.slaHours ?? 24,
        // Absent when the server cannot say; the scraper then enforces its own
        // default of 3, so the popup states the same.
        minForumAccountAgeMonths: raw?.minForumAccountAgeMonths ?? 3,
        status: raw?.status ?? null,
        code: raw?.code ?? null,
    };
}

/**
 * Every campaign this response says is live, keyed by slug.
 *
 * An EMPTY map from the server is treated as no map at all, on purpose: it
 * carries no information that distinguishes "no campaigns are running" from "the
 * server does not emit this prop to you", and both fall through to the legacy
 * prop -- which for a logged-in visitor is null exactly when the promo is off.
 */
function promoRegistry(page) {
    const promos = page.props.promos;
    if (promos && typeof promos === 'object' && Object.keys(promos).length) {
        const out = {};
        Object.entries(promos).forEach(([slug, campaign]) => {
            out[slug] = normalizeCampaign(slug, campaign);
        });

        return out;
    }

    const legacy = page.props.bhw;
    if (legacy) {
        return { [LEGACY_SLUG]: normalizeCampaign(LEGACY_SLUG, { ...LEGACY_GUEST_CAMPAIGN, ...legacy }) };
    }

    return page.props.auth?.user ? {} : { [LEGACY_SLUG]: { ...LEGACY_GUEST_CAMPAIGN } };
}

/** The campaign whose entry route this visitor clicked one redirect ago, if any. */
function forcedSlug(page) {
    if (typeof page.props.forcePromo === 'string' && page.props.forcePromo !== '') {
        return page.props.forcePromo;
    }

    return page.props.bhwForcePopup === true ? LEGACY_SLUG : null;
}

/**
 * Is the copy for this campaign actually loaded?
 *
 * `translations` is the very object app.js hands vue-i18n, and app.js loads only
 * the active locale (the fallback-messages block is commented out), so a lang
 * group missing from this locale -- or still absent from a rememberForever
 * translation cache that nobody cleared after deploy -- renders the popup's title
 * as the literal string 'promo.popup_title'. To a forum audience that reads as a
 * scam, so a campaign whose copy is not there is not pitched at all.
 *
 * BHW included. It used to be exempt, on the grounds that its group ships in
 * every locale -- but the server also withholds both promo groups from visitors
 * it does not count as promo visitors, and a page rendered for one of those
 * (a week-old edge-cached guest page, say) reaching the popup printed
 * 'bhw.popup_title' at the very people the offer is for.
 */
function copyAvailable(page, group) {
    return typeof page.props.translations?.[group]?.popup_title === 'string';
}

// localStorage throws outright in Safari private mode and when a user has
// blocked site data. A visitor we cannot remember is shown the popup under the
// normal rules rather than not at all.
function readBucket(key) {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(key) ?? '');

        return parsed && typeof parsed === 'object' ? {
            n: Number(parsed.n) || 0,
            last: Number(parsed.last) || 0,
            dismissed: Number(parsed.dismissed) || 0,
        } : null;
    } catch {
        return null;
    }
}

function readState(slug) {
    return readBucket(STORAGE_PREFIX + slug)
        // Migration, not fallback: every visitor already pitched BHW carries the
        // old bucket, and ignoring it re-pitches everyone who said no.
        ?? (slug === LEGACY_SLUG ? readBucket(LEGACY_STORAGE_KEY) : null)
        ?? { n: 0, last: 0, dismissed: 0 };
}

function writeState(slug, state) {
    try {
        window.localStorage.setItem(STORAGE_PREFIX + slug, JSON.stringify(state));

        // Dual-written for one release, for the same reason the server dual-emits
        // the props: a visitor who dismisses on this bundle and then loads a
        // cached one must stay dismissed.
        if (slug === LEGACY_SLUG) {
            window.localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(state));
        }
    } catch {
        // Ignore: the frequency cap degrades, nothing else does.
    }
}

function sessionPitched() {
    try {
        return window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
        return null;
    }
}

function rememberSessionPitch(slug) {
    try {
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, slug);
    } catch {
        // Ignore: the cross-campaign guard degrades to the per-campaign caps.
    }
}

// Without these, "the popup never showed" and "the popup showed and nobody
// clicked" are indistinguishable, and the conclusion is "forum traffic doesn't
// convert". GTM is already initialised in AppLayout.
function push(event, extra = {}) {
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event, ...extra });
    } catch {
        // Never let instrumentation break the page.
    }
}

/**
 * Both event vocabularies, for one release.
 *
 * `promo_popup_*` is the one to build on -- it carries the campaign. The
 * `bhw_popup_*` events stay because they are the promo's only impression signal
 * and whatever is watching them was built before this file was: renaming them
 * takes the detector down at the same moment as the thing it detects. They fire
 * for BHW only, so the existing series keeps meaning what it meant instead of
 * quietly becoming a sum over campaigns.
 */
function track(suffix, slug, extra = {}, legacyExtra = {}) {
    push(`promo_popup_${suffix}`, { promo_campaign: slug, ...extra });

    if (slug === LEGACY_SLUG) {
        push(`bhw_popup_${suffix}`, legacyExtra);
    }
}

/** The consent banner, by its OWN named container -- a dependency that fails
 *  loudly if the vendor renames it, rather than silently suppressing us. */
function consentBannerVisible() {
    const cs = document.getElementById('cookiescript_injected');
    return !! cs && cs.offsetParent !== null;
}

/**
 * Live campaign registry from the server, for visitors whose HTML may be stale.
 *
 * Resolves to a map on success and to NULL when the endpoint could not be reached
 * or does not exist -- the difference matters: a map that simply lacks a slug
 * means that campaign is off, and must never be second-guessed.
 */
function fetchPromoStatus() {
    if (! promoStatusPromise) {
        promoStatusPromise = fetch('/api/promo/status', {
            headers: { Accept: 'application/json' },
            credentials: 'omit',
        })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                const campaigns = data?.campaigns;

                return campaigns && typeof campaigns === 'object' ? campaigns : null;
            })
            .catch(() => null);
    }

    return promoStatusPromise;
}

/**
 * The pre-multi-campaign status endpoint, kept as BHW's backstop.
 *
 * Only consulted when /api/promo/status could not answer at all. That endpoint is
 * newer than the bundle that may be asking, and "the new endpoint 404s" must not
 * be able to switch the live BHW offer off for every guest.
 */
function fetchLegacyStatus() {
    if (! legacyStatusPromise) {
        legacyStatusPromise = fetch('/api/bhw/status', {
            headers: { Accept: 'application/json' },
            credentials: 'omit',
        })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => (data?.enabled === true ? data : null))
            .catch(() => null);
    }

    return legacyStatusPromise;
}

/**
 * Which campaign this visitor is here for, and whether they asked.
 *
 * A strict ladder, resolving to exactly one campaign: the entry route they just
 * clicked, then the live utm_source, then last-touch, then first-touch. Two
 * campaigns can both have a claim on a visitor -- first-touch BHW, live click
 * Cracked -- and showing both would be two overlays fighting over one screen, so
 * the strongest signal wins outright.
 *
 * HandleInertiaRequests pulls the force flag, so it is true for one page and never
 * again, and it can only ride on a render that is never edge-cached (see the
 * middleware) -- which is what makes it trustworthy enough to skip the status
 * round-trip in promoIsLive().
 */
function resolveCampaign(page, registry) {
    const forced = forcedSlug(page);
    if (forced) {
        // Not filtered through the registry: a guest's props carry no registry at
        // all, and this visitor demonstrably just clicked this campaign's link.
        // promoIsLive() settles whether it is still on.
        return { slug: forced, forced: true };
    }

    const slug = attributedPromoSlug(registry);
    if (slug) {
        return { slug, forced: false };
    }

    // Nothing LIVE claims this visitor. For a logged-in one that also covers "the campaign they
    // belong to is switched off" -- the server omits a disabled campaign from the registry
    // entirely -- and the two have to stay distinguishable, because blockedReason() reports
    // promo_disabled to GTM and deliberately never reports not_attributed. Only BlackHatWorld's
    // vocabulary is known client-side, which is exactly the set the single-campaign version could
    // report on, so this restores that signal and adds nothing. blockedReason() still answers
    // promo_disabled for it (the slug is absent from the registry), so nothing can be shown.
    if (page.props.auth?.user) {
        return { slug: attributedPromoSlug([LEGACY_GUEST_CAMPAIGN]), forced: false };
    }

    return { slug: null, forced: false };
}

/**
 * The gates that are settled the moment the page loads, as null (may show) or a
 * short reason string. Visibility and competing overlays are NOT here: they change
 * while we wait out the delay, so they are checked once, immediately before the
 * popup opens, in lateBlockedReason().
 *
 * The ORDER is the compatibility surface: it is the order the single-campaign
 * version used, and every reason string it can return is one GTM already receives.
 */
function blockedReason(page, slug, forced, registry) {
    if (! ALLOWED.has(page.component)) {
        return 'page_not_allowed';
    }
    if (! slug) {
        return 'not_attributed';
    }

    const known = registry[slug];

    // Kill switch, for the case we can settle without a request: a logged-in
    // render is never an edge-cache hit (it carries `"user":{`, which
    // EdgeCacheHeaders refuses to mark cacheable), so a campaign missing from a
    // logged-in visitor's registry really means it is off. The remaining case --
    // a guest who was not forced here -- is settled asynchronously in promoIsLive().
    if (page.props.auth?.user && ! known) {
        return 'promo_disabled';
    }
    if (page.props.auth?.user?.banned === true) {
        return 'banned';
    }
    if (TERMINAL_STATUSES.has(known?.status)) {
        return 'claim_terminal';
    }
    if (! copyAvailable(page, promoMessageGroup(known ?? { slug }))) {
        return 'copy_unavailable';
    }

    // Politeness only, so a deliberate click outranks it: the entry route is
    // reached by clicking the forum link or the popup's own CTA, and the CTA
    // records a dismissal on its way out. Enforcing either here would mean the
    // code the CTA just minted is never shown to the person who asked for it.
    if (! forced) {
        // One promo pitch per visit. Without this, a visitor carrying two
        // campaigns' attribution is pitched by the second one the moment the
        // first one's 24 h gate is what stopped it.
        const pitched = sessionPitched();
        if (pitched && pitched !== slug) {
            return 'other_promo_pitched';
        }

        const state = readState(slug);
        if (state.dismissed === 1) {
            return 'dismissed';
        }
        if (state.n >= MAX_SHOWINGS) {
            return 'cap_reached';
        }
        if (state.n > 0 && Date.now() - state.last < MIN_GAP_MS) {
            return 'too_soon';
        }
    }

    return null;
}

export function usePromoPopup() {
    const page = usePage();
    const show = ref(false);
    const campaign = activePromo;

    let timer = null;
    let openSlug = null;
    const stopListening = [];

    const clearTimer = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };

    /** Everything that can flip between "schedule" and "open". */
    const lateBlockedReason = () => {
        if (document.visibilityState !== 'visible') {
            return 'hidden_tab';
        }
        if (anyAppModalOpen.value) {
            return 'app_modal_open';
        }
        if (consentBannerVisible()) {
            return 'consent_banner_open';
        }
        return null;
    };

    /**
     * The kill switch, for the one case blockedReason() cannot settle: a guest on a
     * page that may have been served from the edge cache. Returns the campaign to
     * show (enriched with whatever the server says right now) or null.
     *
     * Resolves straight through for everyone whose props were already checked and
     * are known fresh -- a logged-in visitor, or a forced visitor whose campaign is
     * in the registry.
     */
    const promoIsLive = async (slug, forced, known) => {
        if (known && (forced || page.props.auth?.user)) {
            return known;
        }

        const live = await fetchPromoStatus();
        if (live) {
            return live[slug] ? normalizeCampaign(slug, { ...known, ...live[slug] }) : null;
        }

        // Fail closed for everything the legacy endpoint cannot speak for. A
        // network blip must not pitch a promo that may be over; the visitor can
        // still click the entry link, which re-forces the popup on a response that
        // is guaranteed fresh.
        if (slug !== LEGACY_SLUG) {
            return null;
        }

        const legacy = await fetchLegacyStatus();

        return legacy ? normalizeCampaign(slug, { ...LEGACY_GUEST_CAMPAIGN, ...known, ...legacy }) : null;
    };

    const reveal = (resolved) => {
        const state = readState(resolved.slug);
        writeState(resolved.slug, { ...state, n: state.n + 1, last: Date.now() });
        rememberSessionPitch(resolved.slug);

        openSlug = resolved.slug;
        activePromo.value = resolved;
        show.value = true;

        track('shown', resolved.slug, { promo_page: page.component }, { bhw_page: page.component });
    };

    /** `attempt` is only ever set by the forced retry below -- see FORCED_RETRY_MS. */
    const schedule = (attempt = 0) => {
        clearTimer();

        if (typeof window === 'undefined' || show.value) {
            return;
        }

        const registry = promoRegistry(page);
        const { slug, forced } = resolveCampaign(page, registry);
        const reason = blockedReason(page, slug, forced, registry);
        if (reason) {
            // Only report suppression for visitors who were actually candidates.
            // Every other page view of every other visitor would drown the signal.
            if (reason !== 'page_not_allowed' && reason !== 'not_attributed') {
                track(
                    'suppressed',
                    slug,
                    { promo_reason: reason, promo_page: page.component },
                    { bhw_reason: reason, bhw_page: page.component },
                );
            }
            return;
        }

        const firstDelay = forced ? DELAY_FORCED_MS : DELAY_ELSEWHERE_MS;

        timer = setTimeout(async () => {
            timer = null;

            // Guests elsewhere on the site: their HTML may be a week-old edge hit, so
            // the kill switch has to be confirmed live before we promise anyone money.
            const live = await promoIsLive(slug, forced, registry[slug]);
            if (! live) {
                track(
                    'suppressed',
                    slug,
                    { promo_reason: 'promo_disabled', promo_page: page.component },
                    { bhw_reason: 'promo_disabled', bhw_page: page.component },
                );
                return;
            }

            // Deliberately the last thing before opening: the tab and the overlay
            // state can both change while the delay and the await are outstanding.
            const late = lateBlockedReason();
            if (late) {
                if (forced && attempt < FORCED_RETRY_LIMIT) {
                    schedule(attempt + 1);
                    return;
                }
                track(
                    'suppressed',
                    slug,
                    { promo_reason: late, promo_page: page.component },
                    { bhw_reason: late, bhw_page: page.component },
                );
                return;
            }
            reveal(live);
        }, attempt === 0 ? firstDelay : FORCED_RETRY_MS);
    };

    const close = (suffix) => {
        const slug = openSlug ?? activePromo.value?.slug ?? LEGACY_SLUG;

        show.value = false;
        writeState(slug, { ...readState(slug), dismissed: 1 });
        openSlug = null;

        track(suffix, slug, {});
    };

    const dismiss = () => close('dismissed');
    const engage = () => close('engaged');

    onMounted(() => {
        schedule();

        // router.on touches `document`, so it can only be wired after mount --
        // AppLayout's setup also runs during SSR.
        //
        // A navigation cancels any pending show (the visitor has moved on) and
        // re-evaluates against the page they actually landed on. AppLayout is a
        // persistent Inertia layout, so onMounted alone fires only once.
        // Wrapped rather than passed by reference: Inertia hands the listener an
        // event object, which schedule() would read as a retry counter.
        stopListening.push(router.on('start', clearTimer));
        stopListening.push(router.on('navigate', () => {
            // ALLOWED bounds where the popup may OPEN; without this it would not bound
            // where it may REMAIN. `show` lives in the persistent layout, so a visitor
            // who gets the popup on the homepage and presses Back onto /addfunds would
            // otherwise be staring at a EUR 3.00 overlay on top of a payment form.
            // Not close(): being navigated away from is not the visitor saying no, so
            // it must not burn their one dismissal.
            if (show.value && ! ALLOWED.has(page.component)) {
                show.value = false;
                openSlug = null;
            }

            schedule();
        }));
    });

    onBeforeUnmount(() => {
        clearTimer();
        stopListening.forEach((stop) => stop());
        stopListening.length = 0;
    });

    return { show, campaign, dismiss, engage };
}

/**
 * The pre-multi-campaign name, kept so AppLayout (and any cached bundle built
 * against it) keeps resolving. `usePromoPopup` is the one to import.
 */
export const useBhwPopup = usePromoPopup;
